/**
 * The 44px column at the end of a register row.
 *
 * The DNS check used to be the web cell itself, which meant the one column that
 * looked like a link was actually a button that opened a dialog - and it had to
 * call `stopPropagation` because the `<tr>` around it navigated. The cell is a
 * plain link to the site now and the check is an entry here, where it says what
 * it does.
 */
import { Link } from "react-router-dom";
import { ArrowUpRight, Copy, ExternalLink, Globe, MoreHorizontal } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { ArListRow } from "../../../lib/ariregister";
import { webHref } from "../../../lib/webUrl";
import { Pop, useClosePops } from "../../shell/Pop";
import { useToast } from "../../ui";

export function EntityRowMenu({ row, onCheckDomain }: { row: ArListRow; onCheckDomain: () => void }) {
  const t = useT();
  const toast = useToast();
  const close = useClosePops();
  // Most register rows hold the address with no scheme, which an href reads as
  // a relative path. Null means the field holds no site at all - the DNS check
  // below still runs, since the server parses the value more permissively.
  const siteHref = webHref(row.webUrl);

  async function copyRegCode() {
    close();
    try {
      await navigator.clipboard.writeText(row.regCode);
      toast(t("common.copied"));
    } catch {
      toast(t("common.copyFailed"));
    }
  }

  return (
    // Above the row-covering link overlay, or the menu would never get the click.
    <span className="relative z-[1] justify-self-end">
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={cn("crm-cb", open && "is-on")}
            title={t("common.more")}
            aria-label={t("common.more")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <MoreHorizontal className="size-4" />
          </button>
        )}
      >
        <Link className="crm-mi" to={row.regCode} onClick={close}>
          <ArrowUpRight className="size-4" />
          {t("ariregister.openCompany")}
        </Link>

        <button type="button" className="crm-mi" onClick={() => void copyRegCode()}>
          <Copy className="size-4" />
          {t("ariregister.copyRegCode")}
        </button>

        {row.webUrl ? (
          <>
            <div className="crm-menu-sep" />
            {siteHref ? (
              <a className="crm-mi" href={siteHref} target="_blank" rel="noreferrer noopener" onClick={close}>
                <ExternalLink className="size-4" />
                {t("leads.openSite")}
              </a>
            ) : null}
            <button
              type="button"
              className="crm-mi"
              onClick={() => {
                close();
                onCheckDomain();
              }}
            >
              <Globe className="size-4" />
              {t("ariregister.dns.check")}
            </button>
          </>
        ) : null}
      </Pop>
    </span>
  );
}
